/**
 * openExternal(url) — safely open a URL that should be handled by the
 * OS's default app (Google Maps, dialer, email client, or the
 * external browser for plain https URLs).
 *
 * iter134k — swapped ``@capacitor/app::App.openUrl`` for
 * ``@capacitor/app-launcher::AppLauncher.openUrl``. The ``@capacitor/app``
 * plugin's ``openUrl`` is a DEEP-LINK RECEIVER (fires ``appUrlOpen``
 * events for incoming URIs), NOT an outgoing launcher — a very
 * common Capacitor SDK footgun. On Android it silently no-ops for
 * non-app URIs, then the code falls through to
 * ``window.location.href = url`` which triggers the exact
 * ``ERR_UNKNOWN_URL_SCHEME`` the user reported.
 *
 * Fallback chain (native):
 *   1. ``AppLauncher.openUrl`` — dispatch to OS default app.
 *   2. If URL is https and step 1 fails, open in Chrome Custom Tabs
 *      via ``Browser.open`` — NEVER inside our WebView.
 *   3. Give up silently rather than crash.
 *
 * Web / desktop fallback is unchanged.
 */
export async function openExternal(url) {
    if (!url) return;
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    const isNative = cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform();

    if (isNative) {
        // 1) AppLauncher — the correct plugin for outgoing URIs.
        try {
            const mod = await import("@capacitor/app-launcher");
            if (mod && mod.AppLauncher && typeof mod.AppLauncher.openUrl === "function") {
                const r = await mod.AppLauncher.openUrl({ url });
                if (r && r.completed) return;
            }
        } catch { /* fall through */ }

        // 2) https fallback via Custom Tabs — outside our WebView.
        if (/^https?:/i.test(url)) {
            try {
                const mod = await import("@capacitor/browser");
                if (mod && mod.Browser && typeof mod.Browser.open === "function") {
                    await mod.Browser.open({ url });
                    return;
                }
            } catch { /* fall through */ }
        }

        // 3) Give up silently — do NOT navigate the WebView to the URL,
        //    that reproduces the intent:// rewrite bug.
        return;
    }

    // Web / desktop / mobile browser — standard new-tab.
    try {
        window.open(url, "_blank", "noopener,noreferrer");
    } catch {
        window.location.href = url;
    }
}
